import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import dynaForms from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { actions as formActions } from '@elab/components/connected/forms/Form/redux';
import {
    actions as globalActions,
    selectors as globalSelectors,
    types as globalTypes,
} from '@elab/core/store/global';
import { actions as uiActions } from '@elab/core/store/ui';
import { createWatcher } from '@elab/core/store/core/saga';
import { selectors as userSelectors } from '@elab/core/store/user';
import { actions as cartActions, selectors as cartSelectors } from '@elab/core/store/cart';
import { selectors as productSelectors, types as productTypes } from '@elab/core/store/product';
import { selectors as configSelectors } from '@elab/core/store/config';
import cartApi from '@elab/core/store/cart/api';
import { selectors as wishlistSelectors } from '@elab/core/store/wishlist';
import wishlistApi from '@elab/core/store/wishlist/api';
import {
    cookieNames,
    getParameterNames,
    mixedCartForbiddenPopupName,
} from '@elab/core/utils/constants';
import { loadCart } from '@elab/core/store/cart/saga';
import * as storage from '@elab/core/utils/storage';
import { setCookie } from '@elab/core/utils/cookie';

import actions from './actions';
import selectors from './selectors';
import types, { storeName } from './types';

function* continueEgiftcartSubmit() {
    const egiftCardCode = yield select(cartSelectors.getEGiftCardCode);
    yield put(uiActions.closeDialogByName(mixedCartForbiddenPopupName));
    yield call(giftCardSubmit, egiftCardCode);
}

export function* preSubmit({ payload: { code, mixedcartforbidden } }) {
    if (mixedcartforbidden) {
        const isCartEmpty = yield select(cartSelectors.isEmpty);

        if (isCartEmpty) {
            yield call(giftCardSubmit, code);
        } else {
            yield put(cartActions.setEGiftCardCode(code));
            yield put(uiActions.openDialog({ name: mixedCartForbiddenPopupName }));
        }
    } else {
        yield call(giftCardSubmit, code);
    }
}

function* giftCardSubmit(code) {
    yield put(actions.setDataFieldValue('product', code));
    yield put(formActions.submit(storeName, {}));
}

export function* defaultSubmit(userId, cartId, url = null, dto, wishlistCode = null) {
    const { errors, egiftCardCartId } = wishlistCode
        ? yield call(wishlistApi.addEGiftCardToCart, wishlistCode, cartId, dto)
        : yield call(cartApi.submitGiftCard, userId, cartId, url, dto);
    if (errors) {
        yield put(actions.setGlobalError(errors[0].message));
    } else {
        if (egiftCardCartId) {
            const baseSiteId = yield select(globalSelectors.getSite);
            const cookieName = `${baseSiteId}${cookieNames.eGiftCardCartEnding}`;
            yield call(setCookie, cookieName, egiftCardCartId, false);
            yield call(storage.set, storage.key.eGiftCardPDPLink, window.location.href);

            const guid = yield select(cartSelectors.getGuid);
            yield put(cartActions.setRegularCartId(guid));
            yield put(cartActions.setGuid(egiftCardCartId));
        }

        yield call(loadCart, {
            skipCreation: true,
            fields: 'CART_REFRESH',
        });

        const redirectUrl = yield select(selectors.getSuccessRedirectLink);
        yield put(globalActions.redirect(redirectUrl));
    }
}

export function* doSubmit(dto, ctx, url) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(cartSelectors.getCartId);
    yield put(globalActions.resetFormsMessages());
    const wishlistCode = new window.URLSearchParams(window.location.search).get(
        getParameterNames.wishlistCodeForAddToCart,
    );

    yield call(defaultSubmit, userId, cartId, url, dto, wishlistCode);
}

export function* getPrefillDataFromStorage() {
    const isMultiMode = yield select(globalSelectors.multipleWishlistsEnabled);
    const fromStorage = yield select(configSelectors.prefillFromStorageOnEgiftcardPDP, isMultiMode);

    if (fromStorage) {
        const prefillData = yield call(storage.get, storage.key.prepopulatedData, true);
        return prefillData || {};
    }

    return {};
}

export function* preFillFormFields() {
    const wishlistCode = new window.URLSearchParams(window.location.search).get(
        getParameterNames.wishlistCodeForAddToCart,
    );
    let productCode = yield select(productSelectors.getProductCode);
    if (!productCode) {
        yield take(productTypes.SET_PRODUCT);
        productCode = yield select(productSelectors.getProductCode);
    }
    let { egiftCardInfo } = (yield select(wishlistSelectors.getEntry, productCode)) || {};
    if (!wishlistCode || !egiftCardInfo) {
        egiftCardInfo = yield call(getPrefillDataFromStorage);
        yield call(storage.remove, storage.key.prepopulatedData);
    }

    yield all(
        Object.entries(egiftCardInfo).map(([fieldName, value]) =>
            put(actions.setDataFieldValue(fieldName, value, true)),
        ),
    );
}

export default storeName => formId => [
    ...dynaForms.createSagaFor(
        storeName,
        dynaFormsApi.getFormMetadata(formId || storeName),
        doSubmit,
        null,
        true,
    ),
    createWatcher(globalTypes.RESET_FORMS_MESSAGES, actions.resetGlobalMessage()),
    takeEvery(types.SET_PRODUCT_AND_SUBMIT, preSubmit),
    takeEvery(types.CONTINUE_EGIFTCART_SUBMIT, continueEgiftcartSubmit),
    takeEvery([types.AFTER_INITIALIZE], preFillFormFields),
];
